/**
 * React Query hooks for farms.
 * React Query hooks for the corresponding entity.
 */

import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import { API } from "common/utils";
import { useMemo } from "react";
import { useAppStore } from "stores/useAppStore";
import { Farm } from "./farmTypes";

// Re-export types for convenience
export { farmApi };
export type { Farm };

const farmApi = new API<Farm>("farms");

/**
 * Fetch a single farm by guid
 */
export function useFarm(guid: string | undefined, enabled = true) {
  return useQuery({
    queryKey: queryKeys.farms.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("Farm guid is required");
      }
      return await farmApi.getOne(guid);
    },
    enabled: enabled && !!guid,
  });
}

/**
 * Fetch multiple farms by their IDs
 */
export function useFarmsByIds(farmIds: string[] | undefined, enabled = true) {
  const queries = useQueries({
    queries: (farmIds || []).map((farmId) => ({
      queryKey: queryKeys.farms.detail(farmId),
      queryFn: async () => {
        return await farmApi.getOne(farmId);
      },
      enabled: enabled && farmIds !== undefined && farmIds.length > 0,
    })),
  });

  // Memoize based on dataUpdatedAt to avoid creating new array references
  // when the underlying data hasn't changed (prevents infinite re-render loops)
  const dataKey = queries.map((q) => q.dataUpdatedAt).join(",");
  const farms = useMemo(
    () =>
      queries
        .map((query) => query.data)
        .filter((farm): farm is Farm => farm !== undefined),
    [dataKey]
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    data: farms,
    isLoading,
    isError,
    queries,
  };
}

/**
 * Get the current farm using Zustand store
 */
export function useCurrentFarm() {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  return useFarm(currentFarmId ?? undefined);
}

/**
 * Get all farms for the current user.
 * This is a convenience hook that combines user data and useFarmsByIds.
 * Replaces useFarmsCompat which used cache reading without subscriptions.
 */
export function useUserFarms() {
  const user = useAppStore((state) => state.user);
  const farmIds = user?.farms;

  const {
    data: farms,
    isLoading: farmsLoading,
    isError,
    queries,
  } = useFarmsByIds(farmIds, !!user);

  return useMemo(
    () => ({
      data: farms,
      isLoading: farmsLoading,
      isError,
      queries,
    }),
    [farms, farmsLoading, isError, queries]
  );
}

/**
 * Create a new farm
 */
export function useCreateFarm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (farm: Farm) => {
      const res = await farmApi.createOne(farm);
      const newFarm = await farmApi.getOne(res.guid);
      return newFarm;
    },
    onSuccess: async (newFarm) => {
      queryClient.setQueryData(queryKeys.farms.detail(newFarm.guid), newFarm);
      await queryClient.invalidateQueries({ queryKey: queryKeys.farms.all });
      // Update user's farms array in Zustand store so useUserFarms updates
      useAppStore.getState().addFarmToUser(newFarm.guid);
    },
  });
}

/**
 * Update an existing farm
 */
export function useUpdateFarm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (farm: Farm) => {
      await farmApi.updateOne(farm);
      const updatedFarm = await farmApi.getOne(farm.guid);
      return updatedFarm;
    },
    onMutate: async (farm) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.farms.detail(farm.guid),
      });

      const previousFarm = queryClient.getQueryData<Farm>(
        queryKeys.farms.detail(farm.guid)
      );

      if (previousFarm) {
        queryClient.setQueryData<Farm>(queryKeys.farms.detail(farm.guid), {
          ...previousFarm,
          ...farm,
        });
      }

      return { previousFarm };
    },
    onError: (_err, farm, context) => {
      if (context?.previousFarm) {
        queryClient.setQueryData(
          queryKeys.farms.detail(farm.guid),
          context.previousFarm
        );
      }
    },
    onSuccess: async (updatedFarm) => {
      queryClient.setQueryData(
        queryKeys.farms.detail(updatedFarm.guid),
        updatedFarm
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.farms.all });
    },
  });
}

/**
 * Delete a farm
 */
export function useDeleteFarm() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (farmGuid: string) => {
      await farmApi.deleteOne(farmGuid);
      return farmGuid;
    },
    onSuccess: async (farmGuid) => {
      queryClient.removeQueries({ queryKey: queryKeys.farms.detail(farmGuid) });
      await queryClient.invalidateQueries({ queryKey: queryKeys.farms.all });

      // Update user's farms array in Zustand store
      useAppStore.getState().removeFarmFromUser(farmGuid);

      // Clear currentFarmId if deleted farm was current
      const { currentFarmId, setCurrentFarmId } = useAppStore.getState();
      if (currentFarmId === farmGuid) {
        setCurrentFarmId(null);
      }
    },
  });
}

/**
 * Helper function to reset farms query cache
 */
export function resetFarmsQueryCache(
  queryClient: ReturnType<typeof useQueryClient>
): void {
  queryClient.removeQueries({ queryKey: ["farm"] });
  queryClient.removeQueries({ queryKey: queryKeys.farms.all });
}

/**
 * Hook wrapper for resetFarmsQueryCache
 */
export function useResetFarmsQueryCache() {
  const queryClient = useQueryClient();
  return () => resetFarmsQueryCache(queryClient);
}

/**
 * Fetch a farm by ID and populate React Query cache.
 * Used for prefetching during initialization.
 * @param farmId - The farm ID to fetch
 * @param queryClient - React Query client instance
 * @returns The fetched farm
 */
export async function fetchFarmByIdQuery(
  farmId: string,
  queryClient: ReturnType<typeof useQueryClient>
): Promise<Farm> {
  const farm = await farmApi.getOne(farmId);

  // Update React Query cache
  queryClient.setQueryData(queryKeys.farms.detail(farm.guid), farm);

  return farm;
}
